import { Features, UserRole } from "../../../Enums/companyStatus";
import { hasPermission, hasRole } from "../../../Helpers/authHelpers";
import { useDocumentCraftingCapabilities } from "../../../Query/useDocumentCrafting";

export const DOCUMENT_CRAFTING_PATH = "/document-crafting";

export const DOCUMENT_CRAFTING_FEATURES = [Features.DOCUMENT_CRAFTING];

const query = (params: Record<string, string | number | undefined>) => {
  const search = new URLSearchParams();
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value !== undefined && value !== "") search.set(key, String(value));
  });
  const text = search.toString();
  return text ? `?${text}` : "";
};

export const documentCraftingPaths = {
  list: DOCUMENT_CRAFTING_PATH,
  /** `duplicate` pre-fills the form from a saved revision. */
  create: (options: {
    type?: string;
    duplicate?: { documentId: number; revisionId: number };
  } = {}) =>
    `${DOCUMENT_CRAFTING_PATH}/new${query({
      type: options.type,
      duplicate: options.duplicate
        ? `${options.duplicate.documentId}:${options.duplicate.revisionId}`
        : undefined,
    })}`,
  /** `revision` is the revision number (not its id); none = the latest. */
  document: (documentId: number, revision?: number | null) =>
    `${DOCUMENT_CRAFTING_PATH}/${documentId}${query({
      revision: revision ?? undefined,
    })}`,
  /** `base` is a revision id; `field` focuses a form field (quality notes). */
  revise: (
    documentId: number,
    options: { base?: number; field?: string } = {},
  ) =>
    `${DOCUMENT_CRAFTING_PATH}/${documentId}/revise${query({
      base: options.base,
      field: options.field,
    })}`,
  run: (runId: number) => `${DOCUMENT_CRAFTING_PATH}/runs/${runId}`,
  settings: `${DOCUMENT_CRAFTING_PATH}/settings`,
};

/** Parses `?duplicate=<documentId>:<revisionId>`. */
export const parseDuplicateParam = (
  value: string | null,
): { documentId: number; revisionId: number } | null => {
  const match = value?.match(/^(\d+):(\d+)$/);
  return match
    ? { documentId: Number(match[1]), revisionId: Number(match[2]) }
    : null;
};

/** Whether the user may open the module at all (JWT claims; admins always). */
export const canUseDocumentCrafting = () =>
  hasRole(UserRole.ADMIN) || hasPermission(Features.DOCUMENT_CRAFTING);

/**
 * What the user can do in the module. `canUse` comes from the JWT at once;
 * the rest needs `GET /capabilities/` (workspace admins are not visible in
 * the token) and is false until it has loaded.
 */
export const useDocumentCraftingAccess = () => {
  const isAdmin = hasRole(UserRole.ADMIN);
  const capabilities = useDocumentCraftingCapabilities();
  const data = capabilities.data;
  return {
    isAdmin,
    canUse: data ? data.can_use : canUseDocumentCrafting(),
    hasWorkspace: data?.has_workspace ?? true,
    workspaceId: data?.workspace_id ?? null,
    canManageWorkspace: data?.can_manage_workspace ?? false,
    defaultModelId: data?.default_model_id ?? null,
    isLoading: capabilities.isLoading,
    isError: capabilities.isError,
    refetch: capabilities.refetch,
  };
};
